import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config/api';
import { isRemembered, remember } from '../utils/affiliateAuth';
import AdminNav from './AdminNav';
import './AffiliateAnalytics.css';

// Per-creator commission vs views for one week / month / all time
// (GET /api/stripe/affiliate-analytics). Shares the /affiliates password.
const OVERVIEW_PASSWORD = '0987';

type Period = 'week' | 'month' | 'all';

interface AnalyticsRow {
  route: string;
  igHandle: string;
  status: string;
  stage: string;
  country: string;
  commission: number;
  revenue: number;
  conversions: number;
  views: number;
  reels: number;
  commissionPrev: number | null;
  viewsPrev: number | null;
  error: string | null;
}

interface AnalyticsData {
  period: Period;
  offset: number;
  start: string | null;
  end: string | null;
  partial: boolean;
  prevStart: string | null;
  prevEnd: string | null;
  cachedAt: number | null;
  rows: AnalyticsRow[];
  totals: {
    commission: number;
    revenue: number;
    conversions: number;
    views: number;
    reels: number;
    commissionPrev: number | null;
    viewsPrev: number | null;
  };
}

type SortKey = 'commission' | 'conversions' | 'revenue' | 'views' | 'reels' | 'per10k' | 'commissionDelta' | 'viewsDelta';

const money = (n: number) =>
  '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compact = (n: number) =>
  n >= 1e6 ? (n / 1e6).toFixed(1) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(1) + 'K' : String(n);
const per10k = (commission: number, views: number) => (views > 0 ? (commission / views) * 10000 : null);

// Percent change vs the previous period, or null when there's nothing to compare.
function delta(cur: number, prev: number | null): number | null {
  if (prev === null) return null;
  if (prev === 0) return cur === 0 ? 0 : null;
  return ((cur - prev) / prev) * 100;
}

function DeltaChip({ cur, prev }: { cur: number; prev: number | null }) {
  if (prev === null) return <span className="aa__delta aa__delta--none">–</span>;
  const d = delta(cur, prev);
  if (d === null) return <span className="aa__delta aa__delta--up">new</span>;
  if (Math.abs(d) < 0.5) return <span className="aa__delta aa__delta--flat">0%</span>;
  return (
    <span className={`aa__delta ${d > 0 ? 'aa__delta--up' : 'aa__delta--down'}`}>
      {d > 0 ? '▲' : '▼'} {Math.abs(d).toFixed(0)}%
    </span>
  );
}

function periodLabel(d: AnalyticsData | null, period: Period): string {
  if (period === 'all' || !d || !d.start || !d.end) return 'All time';
  const s = new Date(d.start);
  const e = new Date(new Date(d.end).getTime() - 1);
  const fmt = (x: Date, withYear: boolean) =>
    x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(withYear ? { year: 'numeric' } : {}), timeZone: 'UTC' });
  if (period === 'month') {
    return s.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  }
  return `${fmt(s, false)} – ${fmt(e, true)}`;
}

const AffiliateAnalytics: React.FC = () => {
  const [authed, setAuthed] = useState(() => isRemembered('overview', OVERVIEW_PASSWORD));
  const [password, setPassword] = useState('');
  const [period, setPeriod] = useState<Period>('week');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('commission');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showIdle, setShowIdle] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!authed) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/api/stripe/affiliate-analytics?period=${period}&offset=${offset}`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        if (!j.success) throw new Error(j.error || 'Request failed');
        setData(j.data);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authed, period, offset]);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    const list = data.rows.filter((r) => {
      if (!showIdle && r.commission === 0 && r.views === 0 && r.reels === 0) return false;
      if (q && !r.route.toLowerCase().includes(q) && !r.igHandle.toLowerCase().includes(q)) return false;
      return true;
    });
    const value = (r: AnalyticsRow): number => {
      switch (sortKey) {
        case 'per10k': return per10k(r.commission, r.views) ?? -1;
        case 'commissionDelta': return delta(r.commission, r.commissionPrev) ?? -Infinity;
        case 'viewsDelta': return delta(r.views, r.viewsPrev) ?? -Infinity;
        case 'conversions': return r.conversions;
        case 'revenue': return r.revenue;
        case 'views': return r.views;
        case 'reels': return r.reels;
        default: return r.commission;
      }
    };
    return [...list].sort((a, b) => {
      const cmp = value(a) - value(b) || b.commission - a.commission || b.views - a.views;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir, showIdle, query]);

  const maxCommission = useMemo(() => Math.max(0, ...rows.map((r) => r.commission)), [rows]);
  const maxViews = useMemo(() => Math.max(0, ...rows.map((r) => r.views)), [rows]);

  const changeSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };
  const arrow = (key: SortKey) => (sortKey === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '');
  const changePeriod = (p: Period) => { setPeriod(p); setOffset(0); };

  if (!authed) {
    return (
      <div className="aa">
        <form
          className="aa__login"
          onSubmit={(e) => {
            e.preventDefault();
            if (password === OVERVIEW_PASSWORD) { setAuthed(true); remember('overview', OVERVIEW_PASSWORD); }
            else setError('Incorrect password');
          }}
        >
          <h1 className="aa__login-title">Creator Analytics</h1>
          <input
            id="aa-password"
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="aa__input"
            autoFocus
          />
          {error && <p className="aa__error">{error}</p>}
          <button type="submit" className="aa__btn">Enter</button>
        </form>
      </div>
    );
  }

  const t = data?.totals;
  const hasPrev = period !== 'all';
  // In-progress periods are compared with the previous period up to the same elapsed point.
  const prevWord = period === 'month' ? 'month' : 'week';
  const vsLabel = data?.partial ? `vs same point last ${prevWord}` : `vs previous ${prevWord}`;

  return (
    <div className="aa">
      <div className="aa__container">
        <header className="aa__header">
          <div>
            <h1 className="aa__title">Creator Analytics</h1>
            <p className="aa__subtitle">
              Commission and views per creator, sorted by commission.
            </p>
            <AdminNav />
          </div>
          <div className="aa__controls">
            <div className="aa__seg" role="tablist" aria-label="Period">
              {(['week', 'month', 'all'] as Period[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  role="tab"
                  aria-selected={period === p}
                  className={`aa__seg-btn${period === p ? ' is-active' : ''}`}
                  onClick={() => changePeriod(p)}
                >
                  {p === 'week' ? 'Week' : p === 'month' ? 'Month' : 'All time'}
                </button>
              ))}
            </div>
            {hasPrev && (
              <div className="aa__nav">
                <button type="button" className="aa__nav-btn" aria-label="Previous period" onClick={() => setOffset(offset - 1)} disabled={loading}>‹</button>
                <span className="aa__nav-label">
                  {periodLabel(data && data.period === period ? data : null, period)}
                  {data?.partial && data.period === period && <span className="aa__badge">in progress</span>}
                </span>
                <button type="button" className="aa__nav-btn" aria-label="Next period" onClick={() => setOffset(offset + 1)} disabled={loading || offset >= 0}>›</button>
              </div>
            )}
          </div>
        </header>

        <section className="aa__tiles">
          <div className="aa__tile">
            <span className="aa__tile-label">Commission</span>
            <b className="aa__tile-value">{t ? money(t.commission) : '–'}</b>
            {t && hasPrev && <span className="aa__tile-sub"><DeltaChip cur={t.commission} prev={t.commissionPrev} /> {vsLabel}</span>}
          </div>
          <div className="aa__tile">
            <span className="aa__tile-label">Paid conversions</span>
            <b className="aa__tile-value">{t ? t.conversions.toLocaleString() : '–'}</b>
            <span className="aa__tile-sub">{t ? `${money(t.revenue)} first payments` : ''}</span>
          </div>
          <div className="aa__tile">
            <span className="aa__tile-label">Views</span>
            <b className="aa__tile-value">{t ? compact(t.views) : '–'}</b>
            {t && hasPrev && <span className="aa__tile-sub"><DeltaChip cur={t.views} prev={t.viewsPrev} /> {vsLabel}</span>}
          </div>
          <div className="aa__tile">
            <span className="aa__tile-label">Reels posted</span>
            <b className="aa__tile-value">{t ? t.reels.toLocaleString() : '–'}</b>
          </div>
          <div className="aa__tile">
            <span className="aa__tile-label">Commission per 10K views</span>
            <b className="aa__tile-value">{t && per10k(t.commission, t.views) !== null ? money(per10k(t.commission, t.views) as number) : '–'}</b>
          </div>
        </section>

        <div className="aa__toolbar">
          <input
            id="aa-search"
            type="search"
            className="aa__search"
            placeholder="Filter by creator or Instagram"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <label className="aa__toggle" htmlFor="aa-idle">
            <input id="aa-idle" type="checkbox" checked={showIdle} onChange={(e) => setShowIdle(e.target.checked)} />
            Show creators with no activity
          </label>
          <span className="aa__meta">
            {data ? `${rows.length} creators` : ''}
            {data?.cachedAt ? ` · revenue data as of ${new Date(data.cachedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''}
          </span>
        </div>

        {error && <p className="aa__error">{error}</p>}
        {loading && !data && <p className="aa__loading">Loading… the first load after a deploy can take about 30 seconds.</p>}

        {data && (
          <div className={`aa__table-wrap${loading ? ' is-loading' : ''}`}>
            <table className="aa__table">
              <thead>
                <tr>
                  <th className="aa__th-rank">#</th>
                  <th>Creator</th>
                  <th className="aa__num aa__sortable" onClick={() => changeSort('commission')}>Commission{arrow('commission')}</th>
                  {hasPrev && <th className="aa__num aa__sortable" onClick={() => changeSort('commissionDelta')}>vs prev{arrow('commissionDelta')}</th>}
                  <th className="aa__num aa__sortable" onClick={() => changeSort('conversions')}>Paid{arrow('conversions')}</th>
                  <th className="aa__num aa__sortable" onClick={() => changeSort('views')}>Views{arrow('views')}</th>
                  {hasPrev && <th className="aa__num aa__sortable" onClick={() => changeSort('viewsDelta')}>vs prev{arrow('viewsDelta')}</th>}
                  <th className="aa__num aa__sortable" onClick={() => changeSort('reels')}>Reels{arrow('reels')}</th>
                  <th className="aa__num aa__sortable" onClick={() => changeSort('per10k')}>$/10K views{arrow('per10k')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const eff = per10k(r.commission, r.views);
                  return (
                    <tr key={r.route} className={r.status !== 'active' ? 'is-inactive' : ''}>
                      <td className="aa__rank">{i + 1}</td>
                      <td>
                        <div className="aa__creator">
                          <Link to={`/${r.route}`} className="aa__route">{r.route}</Link>
                          <a href={`https://www.instagram.com/${r.igHandle}/`} target="_blank" rel="noopener noreferrer" className="aa__ig">@{r.igHandle}</a>
                          {r.status !== 'active' && <span className="aa__chip">{r.status}</span>}
                          {r.error && <span className="aa__chip aa__chip--warn" title={r.error}>stale</span>}
                        </div>
                      </td>
                      <td className="aa__num">
                        <div className="aa__barcell">
                          <span className="aa__track"><span className="aa__bar" style={{ width: maxCommission > 0 ? `${(r.commission / maxCommission) * 100}%` : 0 }} /></span>
                          <span className="aa__barval">{money(r.commission)}</span>
                        </div>
                      </td>
                      {hasPrev && <td className="aa__num"><DeltaChip cur={r.commission} prev={r.commissionPrev} /></td>}
                      <td className="aa__num">{r.conversions || '–'}</td>
                      <td className="aa__num">
                        <div className="aa__barcell">
                          <span className="aa__track"><span className="aa__bar aa__bar--views" style={{ width: maxViews > 0 ? `${(r.views / maxViews) * 100}%` : 0 }} /></span>
                          <span className="aa__barval">{r.views ? compact(r.views) : '–'}</span>
                        </div>
                      </td>
                      {hasPrev && <td className="aa__num"><DeltaChip cur={r.views} prev={r.viewsPrev} /></td>}
                      <td className="aa__num">{r.reels || '–'}</td>
                      <td className="aa__num">{eff !== null ? money(eff) : '–'}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={hasPrev ? 9 : 7} className="aa__empty">No commission or posts in this period.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <p className="aa__note">
          Commission is 20% (30% for boosted creators) of each referred customer's first payment, dated by that payment.
          Views are the current view counts of Reels posted in the period, across all of a creator's accounts.
          Weeks start Monday (UTC). A period still in progress is compared with the previous one up to the same point in time. View comparisons lean toward older periods, since their Reels have had longer to collect views.
        </p>
      </div>
    </div>
  );
};

export default AffiliateAnalytics;
